<template>
  <teleport to="body">
    <transition name="deleted-panel">
      <div v-if="open" class="deleted-panel" role="dialog" aria-modal="true" aria-label="Recently deleted inventory">
        <button class="deleted-panel__scrim" type="button" aria-label="Close recently deleted" @click="emit('close')"></button>

        <aside class="deleted-panel__sheet">
          <header class="deleted-panel__header">
            <div>
              <p class="deleted-panel__eyebrow">{{ filteredItems.length }} deleted</p>
              <h2>Recently Deleted</h2>
            </div>
            <button class="deleted-panel__icon-button" type="button" title="Close" aria-label="Close" @click="emit('close')">
              <span class="material-symbols-outlined" aria-hidden="true">close</span>
            </button>
          </header>

          <nav class="deleted-panel__filters" aria-label="Deleted inventory filters">
            <button
              v-for="option in filterOptions"
              :key="option.value"
              type="button"
              :class="{ active: filter === option.value }"
              @click="filter = option.value"
            >
              {{ option.label }}
            </button>
          </nav>

          <section v-if="filteredItems.length === 0" class="deleted-panel__empty">
            <span class="material-symbols-outlined" aria-hidden="true">inventory_2</span>
            <strong>No deleted items</strong>
          </section>

          <section v-else class="deleted-panel__list" aria-label="Deleted inventory items">
            <article v-for="item in filteredItems" :key="`${item.type}:${item.id}`" class="deleted-panel__item">
              <div class="deleted-panel__item-icon" :class="`type-${item.type}`">
                <span class="material-symbols-outlined" aria-hidden="true">{{ typeIcon(item.type) }}</span>
              </div>
              <div class="deleted-panel__item-main">
                <h3 :title="itemLabel(item)">{{ itemLabel(item) }}</h3>
                <p>{{ typeLabel(item.type) }} · {{ formatDeletedAt(item.deletedAt) }}</p>
              </div>
              <button
                type="button"
                class="deleted-panel__restore"
                :disabled="!canRestore || restoringKey === `${item.type}:${item.id}`"
                :title="canRestore ? 'Restore item' : 'Only users with write access can restore items'"
                @click="restore(item)"
              >
                <span class="material-symbols-outlined" aria-hidden="true">restore</span>
                <span>{{ restoringKey === `${item.type}:${item.id}` ? "Restoring" : "Restore" }}</span>
              </button>
            </article>
          </section>
        </aside>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { computed, ref } from "vue";
import { useInventoryStore } from "../../../../stores/inventory";

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  canRestore: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close"]);
const inventoryStore = useInventoryStore();
const filter = ref("all");
const restoringKey = ref(null);

const filterOptions = [
  { value: "all", label: "All" },
  { value: "dewar", label: "Dewars" },
  { value: "rack", label: "Racks" },
  { value: "puck", label: "Pucks" },
  { value: "box", label: "Boxes" },
  { value: "grid", label: "Grids" },
  { value: "lamella", label: "Lamellae" },
];

const filteredItems = computed(() => {
  const items = inventoryStore.deletedEntities || [];
  if (filter.value === "all") return items;
  return items.filter((item) => item.type === filter.value);
});

function itemLabel(item) {
  return String(item.customId || item.serialNo || item.title || item.name || item.id);
}

function typeLabel(type) {
  return `${type.charAt(0).toUpperCase()}${type.slice(1)}`;
}

function typeIcon(type) {
  if (type === "dewar") return "ac_unit";
  if (type === "rack") return "dns";
  if (type === "puck") return "album";
  if (type === "box") return "inventory_2";
  if (type === "grid") return "grid_view";
  if (type === "lamella") return "biotech";
  return "inventory_2";
}

function formatDeletedAt(value) {
  if (!value) return "recently";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "recently";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

async function restore(item) {
  if (!props.canRestore) return;
  const key = `${item.type}:${item.id}`;
  restoringKey.value = key;
  try {
    await inventoryStore.restoreDeletedEntity(item.type, item.id);
  } catch (error) {
    window.alert(error?.message || "Failed to restore inventory item");
  } finally {
    restoringKey.value = null;
  }
}
</script>

<style scoped>
.deleted-panel {
  position: fixed;
  inset: 0;
  z-index: 160;
  display: grid;
  justify-items: end;
}

.deleted-panel__scrim {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(10, 16, 24, 0.44);
}

.deleted-panel__sheet {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  width: min(520px, 100vw);
  height: 100%;
  background: #f7f9fc;
  border-left: 1px solid #d7dee8;
  box-shadow: -18px 0 44px rgba(15, 23, 42, 0.24);
}

.deleted-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 22px 14px;
  border-bottom: 1px solid #dfe6ef;
  background: #ffffff;
}

.deleted-panel__eyebrow {
  margin: 0 0 3px;
  color: #667085;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.deleted-panel__header h2 {
  margin: 0;
  color: #172033;
  font-size: 22px;
  font-weight: 800;
}

.deleted-panel__icon-button {
  display: inline-grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 1px solid #d6dde7;
  border-radius: 6px;
  background: #ffffff;
  color: #334155;
}

.deleted-panel__filters {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding: 12px 16px;
  border-bottom: 1px solid #dfe6ef;
  background: #f7f9fc;
}

.deleted-panel__filters button {
  flex: 0 0 auto;
  height: 30px;
  padding: 0 10px;
  border: 1px solid #d6dde7;
  border-radius: 6px;
  background: #ffffff;
  color: #475569;
  font-size: 12px;
  font-weight: 800;
}

.deleted-panel__filters button.active {
  border-color: #334155;
  background: #334155;
  color: #ffffff;
}

.deleted-panel__empty {
  display: grid;
  place-items: center;
  align-content: center;
  gap: 8px;
  min-height: 220px;
  color: #64748b;
}

.deleted-panel__empty .material-symbols-outlined {
  font-size: 42px;
  color: #aab5c4;
}

.deleted-panel__list {
  display: grid;
  align-content: start;
  gap: 10px;
  overflow-y: auto;
  padding: 14px 16px 20px;
}

.deleted-panel__item {
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #dfe6ef;
  border-radius: 8px;
  background: #ffffff;
}

.deleted-panel__item-icon {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 7px;
  background: #e2e8f0;
  color: #334155;
}

.deleted-panel__item-icon.type-dewar {
  background: #dff3ff;
  color: #0369a1;
}

.deleted-panel__item-icon.type-rack {
  background: #e5e9ff;
  color: #4338ca;
}

.deleted-panel__item-icon.type-puck {
  background: #dcfce7;
  color: #047857;
}

.deleted-panel__item-icon.type-box {
  background: #fef3c7;
  color: #a16207;
}

.deleted-panel__item-icon.type-grid {
  background: #ede9fe;
  color: #6d28d9;
}

.deleted-panel__item-icon.type-lamella {
  background: #ccfbf1;
  color: #0f766e;
}

.deleted-panel__item-main {
  min-width: 0;
}

.deleted-panel__item-main h3 {
  overflow: hidden;
  margin: 0;
  color: #111827;
  font-size: 14px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.deleted-panel__item-main p {
  margin: 3px 0 0;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.deleted-panel__restore {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 94px;
  height: 34px;
  justify-content: center;
  border: 1px solid #a7f3d0;
  border-radius: 6px;
  background: #ecfdf5;
  color: #047857;
  font-size: 12px;
  font-weight: 800;
}

.deleted-panel__restore:disabled {
  cursor: not-allowed;
  border-color: #d6dde7;
  background: #f1f5f9;
  color: #94a3b8;
}

.deleted-panel-enter-active,
.deleted-panel-leave-active {
  transition: opacity 160ms ease;
}

.deleted-panel-enter-from,
.deleted-panel-leave-to {
  opacity: 0;
}

.deleted-panel-enter-active .deleted-panel__sheet,
.deleted-panel-leave-active .deleted-panel__sheet {
  transition: transform 180ms ease;
}

.deleted-panel-enter-from .deleted-panel__sheet,
.deleted-panel-leave-to .deleted-panel__sheet {
  transform: translateX(100%);
}

@media (max-width: 640px) {
  .deleted-panel__sheet {
    width: 100vw;
  }

  .deleted-panel__item {
    grid-template-columns: 34px minmax(0, 1fr);
  }

  .deleted-panel__restore {
    grid-column: 1 / -1;
    width: 100%;
  }
}
</style>
